import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertBox, Button, Icon, Span, useScreenClass } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { BasicPreloader } from '../shared/BasicPreloader';
import {
  getAnonVatInvoices,
  getVatInvoiceAvailable,
  getVatInvoices,
} from '../../apis/vatInvoiceApi';
import {
  trackVatDownloadAttempted,
  trackVatDownloadAttemptFailed,
  trackVatDownloadAttemptSucceeded,
} from '../../apis/newRelicTrackingApi';
import {
  vatInvoiceDownloadAttempted,
  vatInvoiceDownloadAttemptFailed,
  vatInvoiceDownloadAttemptSucceeded,
} from '../../apis/trackingApi';
import useSiteData from '../../hooks/useSiteData';

export const VatInvoiceDownloadButton = ({ order }) => {
  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);
  const [error, setError] = useState(null);
  const [vatInvoicesAvailable, setVatInvoicesAvailable] = useState(false);
  const { orderNumber, shopperId, tenantId, orderPlacedAnonymously } = order;
  const [orderDetailsMessages] = useTranslations(['orderDetailsMessages']);
  const { tenantEnvironment: environment } = useSiteData();
  const screenClass = useScreenClass();

  useEffect(() => {
    getVatInvoiceAvailable({ order, environment }).then(invoiceAvailable => {
      if (invoiceAvailable === true) {
        setVatInvoicesAvailable(true);
      }
    });
  }, [environment, order]);

  // Tell the browser to download the pdf file.
  const downloadPdf = (data, filename, mime) => {
    const blob = new Blob([data], {
      type: mime || 'application/octet-stream',
    });
    const blobURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename);
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  };

  const vatDownloadHandler = async () => {
    setError(null);
    setAwaitingApiResponse(true);

    const metricsAdditionalData = {
      orderNumber,
      tenantId,
      orderPlacedAnonymously,
      shopperId,
    };

    // notify segment for analytics
    vatInvoiceDownloadAttempted(metricsAdditionalData);

    // notify NR for tracking/monitoring
    trackVatDownloadAttempted(metricsAdditionalData);

    try {
      const response = orderPlacedAnonymously
        ? await getAnonVatInvoices({ order, environment })
        : await getVatInvoices({ orderNumber, environment });
      const data = await response.arrayBuffer();
      downloadPdf(
        data,
        `${orderNumber}_${orderDetailsMessages.vatPdfFileNameSuffix}.pdf`,
        'application/pdf'
      );
      setAwaitingApiResponse(false);
      setError(null);
      if (!response.ok) {
        setError(`[${response.status}] response from VAT Download dependency.`);
      }

      // notify segment for analytics
      vatInvoiceDownloadAttemptSucceeded(metricsAdditionalData);

      // notify NR for tracking/monitoring
      trackVatDownloadAttemptSucceeded(metricsAdditionalData);
    } catch (err) {
      setAwaitingApiResponse(false);
      setError(err);
      metricsAdditionalData.error = err;

      // notify segment for analytics
      vatInvoiceDownloadAttemptFailed(metricsAdditionalData);

      // notify NR for tracking/monitoring
      trackVatDownloadAttemptFailed(metricsAdditionalData);
    }
  };

  if (vatInvoicesAvailable) {
    return (
      <>
        <Button
          width={['xs', 'sm'].includes(screenClass) ? 'full-width' : 'standard'}
          onClick={() => vatDownloadHandler()}
          data-testid="download-vat-invoice-button"
        >
          <Span mx={awaitingApiResponse ? 0 : 3}>
            {orderDetailsMessages.vatDownloadText}
            {awaitingApiResponse ? (
              <BasicPreloader size="tiny" ml={2} />
            ) : (
              <Icon iconType="download" size="20p" aria-hidden="true" />
            )}
          </Span>
        </Button>
        {error ? (
          <AlertBox skin="error" mt={3}>
            {orderDetailsMessages.vatDownloadErrorText}
          </AlertBox>
        ) : null}
      </>
    );
  }

  return null;
};

VatInvoiceDownloadButton.propTypes = {
  order: PropTypes.object.isRequired,
};
